import React, { useState, useEffect } from 'react';
import Dialog from '../common/Dialog';
import { useDispatch, useSelector } from 'react-redux';
import { createChangeOrderCostItem } from '../../store/slices/changeOrdersSlice';
import { showToast } from '../utils/Toast';

const ChangeOrderCostItemDialog = ({ isOpen, onClose, onSave, projectId, changeOrderId, scopeId }) => {
  const dispatch = useDispatch();
  const loading = useSelector(state => state.changeOrders.loading);
  const [formData, setFormData] = useState({
    item: '',
    description: '',
    quantity: 1,
    unit: 'EA',
    unitCost: 0,
    builderCost: 0,
    markup: 49.25,
    clientPrice: 0,
    margin: 33,
    // If scopeId is provided, this cost item will be associated with that scope
    costGroup: scopeId || null
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        item: '',
        description: '',
        quantity: 1,
        unit: 'EA',
        unitCost: 0,
        builderCost: 0,
        markup: 49.25,
        clientPrice: 0,
        margin: 33,
        costGroup: scopeId || null
      });
    }
  }, [isOpen, scopeId]);

  // CSS classes
  const labelClasses = "block text-xs uppercase text-gray-500 mb-1";
  const inputClasses = `
    w-full border border-gray-200 px-4 py-2.5 
    focus:outline-none focus:border-blue-500 text-gray-900
    bg-white
  `;
  const textAreaClasses = `
    w-full border border-gray-200 px-4 py-2.5 
    focus:outline-none focus:border-blue-500 text-gray-900
    bg-white resize-none h-24
  `;

  // Helper functions for calculations
  const calculateBuilderCost = (quantity, unitCost) => {
    return (Number(quantity) || 0) * (Number(unitCost) || 0);
  };

  const calculateClientPrice = (builderCost, markup) => {
    return (Number(builderCost) || 0) * (1 + ((Number(markup) || 0) / 100));
  };

  const calculateMargin = (builderCost, clientPrice) => {
    if (!clientPrice || clientPrice <= 0 || !builderCost || builderCost <= 0) return 33;
    return ((clientPrice - builderCost) / clientPrice) * 100;
  };

  // Handle input changes
  const handleChange = (field, value) => {
    const updates = { [field]: value };
    
    if (field === 'quantity' || field === 'unitCost') {
      const quantity = field === 'quantity' ? Number(value) : Number(formData.quantity);
      const unitCost = field === 'unitCost' ? Number(value) : Number(formData.unitCost);
      
      updates.builderCost = calculateBuilderCost(quantity, unitCost);
      
      if (formData.markup) {
        updates.clientPrice = calculateClientPrice(updates.builderCost, formData.markup);
        updates.margin = calculateMargin(updates.builderCost, updates.clientPrice);
      }
    } else if (field === 'markup') {
      const markup = Number(value);
      updates.clientPrice = calculateClientPrice(formData.builderCost, markup);
      updates.margin = calculateMargin(formData.builderCost, updates.clientPrice);
    } else if (field === 'clientPrice') {
      const clientPrice = Number(value);
      updates.margin = calculateMargin(formData.builderCost, clientPrice);
    }

    setFormData(prev => ({
      ...prev,
      ...updates
    }));
  };

  // Save the cost item
  const handleSubmit = async () => {
    try {
      if (!formData.item) {
        showToast('Item name is required', 'error');
        return;
      }

      if (!projectId || !changeOrderId) {
        showToast('Project ID and Change Order ID are required', 'error');
        return;
      }

      // Create the cost item data
      const costItemData = {
        projectId,
        changeOrderId,
        item: formData.item.trim(),
        description: formData.description.trim(),
        quantity: Number(formData.quantity) || 1,
        unit: formData.unit || 'EA',
        unitCost: Number(formData.unitCost) || 0,
        builderCost: Number(formData.builderCost) || 0,
        markup: Number(formData.markup) || 49.25,
        clientPrice: Number(formData.clientPrice) || 0,
        margin: Number(formData.margin) || 33,
        // If we have a scope ID, associate this cost item with that scope
        costGroup: formData.costGroup
      };

      const result = await dispatch(createChangeOrderCostItem(costItemData)).unwrap();
      onSave(result);
      onClose();
    } catch (error) {
      console.error('Failed to save cost item:', error);
      showToast(`Failed to save cost item: ${error.message || error}`, 'error');
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">
            {scopeId ? 'Add Cost Item to Scope' : 'Add New Cost Item'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className={labelClasses}>Item Name</label>
            <input
              type="text"
              value={formData.item}
              onChange={(e) => handleChange('item', e.target.value)}
              className={inputClasses}
              placeholder="Enter item name"
            />
          </div>

          <div>
            <label className={labelClasses}>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className={textAreaClasses}
              placeholder="Enter description"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClasses}>Quantity</label>
              <input
                type="number"
                value={formData.quantity}
                onChange={(e) => handleChange('quantity', e.target.value)}
                className={inputClasses}
                min="0"
              />
            </div>

            <div>
              <label className={labelClasses}>Unit</label>
              <input
                type="text"
                value={formData.unit}
                onChange={(e) => handleChange('unit', e.target.value)}
                className={inputClasses}
                placeholder="EA"
              />
            </div>

            <div>
              <label className={labelClasses}>Unit Cost</label>
              <input
                type="number"
                value={formData.unitCost}
                onChange={(e) => handleChange('unitCost', e.target.value)}
                className={inputClasses}
                min="0"
                step="0.01"
              />
            </div>

            <div>
              <label className={labelClasses}>Builder Cost</label>
              <input
                type="number"
                value={formData.builderCost}
                className={`${inputClasses} bg-gray-50`}
                readOnly
              />
            </div>

            <div>
              <label className={labelClasses}>Markup %</label>
              <input
                type="number"
                value={formData.markup}
                onChange={(e) => handleChange('markup', e.target.value)}
                className={inputClasses}
                min="0"
                step="any"
              />
            </div>

            <div>
              <label className={labelClasses}>Client Price</label>
              <input
                type="number"
                value={formData.clientPrice}
                onChange={(e) => handleChange('clientPrice', e.target.value)}
                className={inputClasses}
                min="0"
                step="0.01"
              />
            </div>

            <div>
              <label className={labelClasses}>Margin %</label>
              <input
                type="number"
                value={formData.margin}
                className={`${inputClasses} bg-gray-50`}
                readOnly
              />
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Cost Item'}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default ChangeOrderCostItemDialog; 